import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Progress } from "@/shared/ui/progress";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Award,
  Calendar,
  Clock,
  AlertTriangle,
  Star,
  TrendingUp,
  CheckCircle2,
  Target,
  FileText,
  ChevronRight,
  Bell,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useNavigate } from "react-router-dom";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
}

interface UserCompetency {
  id: string;
  user_id: string;
  competency_id: string;
  certified_at: string;
  expires_at: string | null;
  status: string;
  training_hours: number | null;
  certificate_number: string | null;
  competency: Competency;
}

interface WorkInstruction {
  id: string;
  name: string;
  checklist_kind: "startup" | "shift_change";
  instruction_competencies: {
    competency: { id: string; name: string; color: string | null } | null;
  }[];
}

interface OperatorCompetencyViewProps {
  currentUserId: string;
  userCompetencies: UserCompetency[];
  competencies: Competency[];
  workInstructions: WorkInstruction[];
  isLoading: boolean;
}

// Certification levels based on number of competencies
const getCertificationLevel = (activeCompetencies: number, totalCompetencies: number) => {
  const percentage = totalCompetencies > 0 ? (activeCompetencies / totalCompetencies) * 100 : 0;
  
  if (percentage >= 90) return { level: "Master", icon: Star, color: "text-warning", bg: "bg-warning/10", description: "Top-tier expert with comprehensive skills" };
  if (percentage >= 70) return { level: "Expert", icon: TrendingUp, color: "text-primary", bg: "bg-primary/10", description: "Highly proficient across most areas" };
  if (percentage >= 50) return { level: "Advanced", icon: Award, color: "text-chart-2", bg: "bg-chart-2/10", description: "Strong foundation with growing expertise" };
  if (percentage >= 25) return { level: "Intermediate", icon: CheckCircle2, color: "text-success", bg: "bg-success/10", description: "Developing skills and certifications" };
  return { level: "Beginner", icon: Target, color: "text-muted-foreground", bg: "bg-muted", description: "Starting your certification journey" };
};

export function OperatorCompetencyView({
  currentUserId,
  userCompetencies,
  competencies,
  workInstructions,
  isLoading,
}: OperatorCompetencyViewProps) {
  const navigate = useNavigate();
  
  const myCompetencies = userCompetencies.filter((uc) => uc.user_id === currentUserId);
  const activeCompetencies = myCompetencies.filter((uc) => uc.status === "active");
  const coverage = competencies.length > 0 ? Math.round((activeCompetencies.length / competencies.length) * 100) : 0;
  const certLevel = getCertificationLevel(activeCompetencies.length, competencies.length);
  const CertIcon = certLevel.icon;

  // Expiring soon (within 30 days)
  const expiringCompetencies = myCompetencies.filter((uc) => {
    if (!uc.expires_at || uc.status !== "active") return false;
    const daysUntil = differenceInDays(new Date(uc.expires_at), new Date());
    return daysUntil > 0 && daysUntil <= 30;
  });

  // Required competencies from work instructions that user doesn't have
  const myCompetencyIds = new Set(activeCompetencies.map((uc) => uc.competency_id));
  const requiredMissing = (() => {
    const allRequired = new Set<string>();
    workInstructions.forEach((wi) => {
      wi.instruction_competencies.forEach((wic) => {
        if (wic.competency) {
          allRequired.add(wic.competency.id);
        }
      });
    });
    return competencies.filter((c) => allRequired.has(c.id) && !myCompetencyIds.has(c.id));
  })();

  // Total training hours
  const totalTrainingHours = activeCompetencies.reduce((sum, uc) => sum + (uc.training_hours || 0), 0);

  // Tasks due count
  const tasksDueCount = requiredMissing.length + expiringCompetencies.length;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Certification Level Hero Card */}
      <Card className={`${certLevel.bg} border-2`} style={{ borderColor: `var(--${certLevel.color.replace('text-', '')})` }}>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className={`w-24 h-24 rounded-full ${certLevel.bg} flex items-center justify-center border-4`} style={{ borderColor: `var(--${certLevel.color.replace('text-', '')})` }}>
              <CertIcon className={`w-12 h-12 ${certLevel.color}`} />
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <h2 className={`text-3xl font-bold ${certLevel.color}`}>{certLevel.level}</h2>
                <Badge variant="outline" className={`${certLevel.color} border-current`}>
                  {coverage}% Complete
                </Badge>
              </div>
              <p className="text-muted-foreground mb-4">{certLevel.description}</p>
              
              <div className="w-full max-w-md mx-auto md:mx-0">
                <Progress value={coverage} className="h-3" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>{activeCompetencies.length} of {competencies.length} certifications</span>
                  <span>{100 - coverage}% to next level</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-4 rounded-lg bg-background/50">
                <Award className="w-6 h-6 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold">{activeCompetencies.length}</p>
                <p className="text-xs text-muted-foreground">Active Certs</p>
              </div>
              <div className="p-4 rounded-lg bg-background/50">
                <Clock className="w-6 h-6 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold">{totalTrainingHours}h</p>
                <p className="text-xs text-muted-foreground">Training</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Due Section */}
      {tasksDueCount > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-warning" />
              Tasks Due
              <Badge className="bg-warning text-warning-foreground">{tasksDueCount}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Expiring Certifications */}
            {expiringCompetencies.map((uc) => (
              <div
                key={uc.id}
                className="flex items-center justify-between p-3 rounded-lg bg-background border border-warning/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  </div>
                  <div>
                    <p className="font-medium">{uc.competency.name}</p>
                    <p className="text-xs text-muted-foreground">Certification expiring soon</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-warning border-warning">
                  {differenceInDays(new Date(uc.expires_at!), new Date())} days left
                </Badge>
              </div>
            ))}

            {/* Required Missing */}
            {requiredMissing.slice(0, 3).map((comp) => (
              <div
                key={comp.id}
                className="flex items-center justify-between p-3 rounded-lg bg-background border border-destructive/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-destructive" />
                  </div>
                  <div>
                    <p className="font-medium">{comp.name}</p>
                    <p className="text-xs text-muted-foreground">Required certification - not yet earned</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-destructive border-destructive">
                  Required
                </Badge>
              </div>
            ))}
            
            {requiredMissing.length > 3 && (
              <p className="text-sm text-muted-foreground text-center">
                + {requiredMissing.length - 3} more required certifications
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Certifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-success" />
              My Certifications
              <Badge variant="secondary">{activeCompetencies.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeCompetencies.length === 0 ? (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No certifications yet</p>
                <p className="text-sm text-muted-foreground">Complete training to earn certifications</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {activeCompetencies.map((uc) => (
                  <div
                    key={uc.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: uc.competency.color }}
                      />
                      <div>
                        <p className="font-medium text-sm">{uc.competency.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Certified {format(new Date(uc.certified_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {uc.expires_at ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(uc.expires_at), "MMM yyyy")}
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-xs text-success border-success">
                          No Expiry
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress to Next Level */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-primary" />
              Progress to Next Level
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Level progression */}
              <div className="space-y-3">
                {[
                  { level: "Master", threshold: 90, icon: Star, color: "text-warning" },
                  { level: "Expert", threshold: 70, icon: TrendingUp, color: "text-primary" },
                  { level: "Advanced", threshold: 50, icon: Award, color: "text-chart-2" },
                  { level: "Intermediate", threshold: 25, icon: CheckCircle2, color: "text-success" },
                  { level: "Beginner", threshold: 0, icon: Target, color: "text-muted-foreground" },
                ].map((lvl) => {
                  const LvlIcon = lvl.icon;
                  const isCurrentLevel = certLevel.level === lvl.level;
                  const isAchieved = coverage >= lvl.threshold;
                  
                  return (
                    <div
                      key={lvl.level}
                      className={`flex items-center gap-3 p-2 rounded-lg ${
                        isCurrentLevel ? "bg-primary/10 border border-primary/30" : ""
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isAchieved ? "bg-primary/10" : "bg-muted"
                      }`}>
                        <LvlIcon className={`w-4 h-4 ${isAchieved ? lvl.color : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${isAchieved ? "" : "text-muted-foreground"}`}>
                          {lvl.level}
                        </p>
                        <p className="text-xs text-muted-foreground">{lvl.threshold}% coverage</p>
                      </div>
                      {isCurrentLevel && (
                        <Badge className="bg-primary text-primary-foreground">Current</Badge>
                      )}
                      {isAchieved && !isCurrentLevel && (
                        <CheckCircle2 className="w-5 h-5 text-success" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Competencies needed */}
              {certLevel.level !== "Master" && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm font-medium mb-2">Competencies needed for next level:</p>
                  <div className="flex flex-wrap gap-2">
                    {competencies
                      .filter((c) => !myCompetencyIds.has(c.id))
                      .slice(0, 5)
                      .map((comp) => (
                        <Badge
                          key={comp.id}
                          variant="outline"
                          className="text-xs"
                          style={{ borderColor: comp.color, color: comp.color }}
                        >
                          {comp.name}
                        </Badge>
                      ))}
                    {competencies.filter((c) => !myCompetencyIds.has(c.id)).length > 5 && (
                      <Badge variant="secondary" className="text-xs">
                        +{competencies.filter((c) => !myCompetencyIds.has(c.id)).length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
